// client/src/utils/pdfClient.ts
// PDF côté navigateur avec jsPDF (zéro backend).
// Assure-toi d'avoir: yarn add jspdf

import jsPDF from "jspdf";
import type { COFFRET } from "../api/coffrets";

function safe(v: any): string {
  return v === null || v === undefined || v === "" ? "—" : String(v);
}

function nStr(v: any): string {
  const n = Number(v);
  return Number.isFinite(n) ? String(n) : "—";
}

// Remplace les espaces insécables
function normalizeNbsp(s: string): string {
  return s.replace(/\u00A0|\u202F/g, " ");
}

function money(v: any): string {
  const n = Number(v);

  if (!Number.isFinite(n)) return "—";

  const formatted = new Intl.NumberFormat("fr-FR", {
    useGrouping: true,
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  }).format(n);

  return `${normalizeNbsp(formatted)} €`;
}

// ✅ Charge l'image ET corrige automatiquement l'orientation EXIF
async function loadImage(
  url: string
): Promise<{ dataUrl: string; w: number; h: number } | null> {
  try {
    const resp = await fetch(url, {
      method: "GET",
      headers: { Accept: "image/*" },
      credentials: "same-origin",
    });

    if (!resp.ok) return null;

    const blob = await resp.blob();

    // ✅ IMPORTANT :
    // createImageBitmap corrige automatiquement
    // les rotations EXIF du téléphone
    const bitmap = await createImageBitmap(blob);

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) return null;

    canvas.width = bitmap.width;
    canvas.height = bitmap.height;

    ctx.drawImage(bitmap, 0, 0);

    const dataUrl = canvas.toDataURL("image/jpeg", 0.95);

    return {
      dataUrl,
      w: canvas.width,
      h: canvas.height,
    };
  } catch (e) {
    console.error("loadImage error:", e);
    return null;
  }
}

export async function buildCoffretPdf(
  coffret: COFFRET
): Promise<Blob> {
  const doc = new jsPDF({
    unit: "pt",
    format: "a4",
    compress: true,
  });

  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  // ========================
  // Fond
  // ========================

  doc.setFillColor(11, 15, 20);
  doc.rect(0, 0, pageW, pageH, "F");

  // ========================
  // Header
  // ========================

  doc.setTextColor(233, 241, 251);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text("COLLECTIONS", 42, 56);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.setTextColor(143, 163, 183);
  doc.text("Famille Reynaud — Fiche spécimen", 42, 74);

  doc.setDrawColor(98, 208, 255);
  doc.setLineWidth(2);
  doc.line(42, 84, pageW - 42, 84);

  // ========================
  // Card
  // ========================

  const cardX = 42;
  const cardY = 100;
  const cardW = pageW - 84;
  const cardH = 620;

  doc.setFillColor(18, 24, 33);
  doc.roundedRect(cardX, cardY, cardW, cardH, 12, 12, "F");

  doc.setDrawColor(26, 33, 48);
  doc.roundedRect(cardX, cardY, cardW, cardH, 12, 12, "S");

  // ========================
  // Titre
  // ========================

  const titleLeft = cardX + 20;
  let cursorY = cardY + 28;

  const nameParts = [
    safe(coffret.genre),
    safe(coffret.espece),
  ].filter((s) => s !== "—");

  const specimenName =
    nameParts.length
      ? nameParts.join(" ").toUpperCase()
      : `SPÉCIMEN (${safe(coffret._id)})`;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.setTextColor(233, 241, 251);

  const wrappedName = doc.splitTextToSize(
    specimenName,
    cardW - 40
  );

  doc.text(wrappedName, titleLeft, cursorY);

  const nameBox = doc.getTextDimensions(wrappedName);

  cursorY += nameBox.h + 10;

  // Pastille verte
  doc.setFillColor(65, 214, 149);
  doc.circle(cardX + cardW - 28, cardY + 28, 6, "F");

  // ========================
  // IMAGE
  // ========================

  const imgUrl = coffret?.numeroCoffret
    ? `/api/image/hd/${coffret.numeroCoffret}-HD.jpg`
    : "";

  if (imgUrl) {
    const img = await loadImage(imgUrl);

    if (img && img.w > 0 && img.h > 0) {
      const isPortrait = img.h > img.w;

      let drawW = 0;
      let drawH = 0;

// ✅ IMAGE VERTICALE
if (isPortrait) {
  // légèrement plus petite pour laisser respirer le texte
  const maxW = 190;
  const maxH = 270;

  const scale = Math.min(
    maxW / img.w,
    maxH / img.h
  );

  drawW = img.w * scale;
  drawH = img.h * scale;
}

      // ✅ IMAGE HORIZONTALE
      else {
        const maxW = cardW - 40;
        const maxH = 260;

        const scale = Math.min(
          maxW / img.w,
          maxH / img.h
        );

        drawW = img.w * scale;
        drawH = img.h * scale;
      }

      const imgX = cardX + (cardW - drawW) / 2;
      const imgY = cursorY;

      // ✅ L'image gardera EXACTEMENT
      // le même sens que sur le site
      doc.addImage(
        img.dataUrl,
        "JPEG",
        imgX,
        imgY,
        drawW,
        drawH,
        "",
        "FAST"
      );

      cursorY = imgY + drawH + 20;
    }
  }

  // ========================
  // Helpers
  // ========================

  const label = (
    t: string,
    x: number,
    y: number
  ) => {
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(169, 183, 198);
    doc.text(t.toUpperCase(), x, y);
  };

  const value = (
    t: string,
    x: number,
    y: number,
    w = 260
  ) => {
    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(233, 241, 251);

    doc.text(
      doc.splitTextToSize(t || "—", w),
      x,
      y
    );
  };

  const header = (
    t: string,
    x: number,
    y: number
  ) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(233, 241, 251);
    doc.text(t, x, y);

    doc.setDrawColor(98, 208, 255);
    doc.setLineWidth(1);
    doc.line(x, y + 4, x + 220, y + 4);
  };

  const colW = 260;
  const colGap = 24;

  const col2X = titleLeft + colW + colGap;

  // ========================
  // Sections
  // ========================

  header("Identification", titleLeft, cursorY);

  cursorY += 18;

  label("Genre", titleLeft, cursorY);
  value(
    safe(coffret.genre),
    titleLeft,
    cursorY + 12,
    colW
  );

  label("Espèce", col2X, cursorY);
  value(
    safe(coffret.espece),
    col2X,
    cursorY + 12,
    colW
  );

  cursorY += 22;

  label("Sexe", titleLeft, cursorY);
  value(
    safe(coffret.sexe),
    titleLeft,
    cursorY + 12,
    colW
  );

  label("Faune", col2X, cursorY);
  value(
    safe(coffret.faune),
    col2X,
    cursorY + 12,
    colW
  );

  cursorY += 22;

  label("CITES", titleLeft, cursorY);
  value(
    safe(coffret.cites),
    titleLeft,
    cursorY + 12,
    colW
  );

  label("Quantité", col2X, cursorY);
  value(
    nStr(coffret.quantite),
    col2X,
    cursorY + 12,
    colW
  );

  cursorY += 30;

  header("Provenance & capture", titleLeft, cursorY);

  cursorY += 18;

  label("Date de capture", titleLeft, cursorY);
  value(
    safe(coffret.dateCapture),
    titleLeft,
    cursorY + 12,
    colW
  );

  label("Détails de capture", col2X, cursorY);
  value(
    safe(coffret.detailsCapture),
    col2X,
    cursorY + 12,
    colW
  );

  cursorY += 30;

  header("Localisation", titleLeft, cursorY);

  cursorY += 18;

  label("Type de collection", titleLeft, cursorY);
  value(
    safe(coffret.type),
    titleLeft,
    cursorY + 12,
    colW
  );

  label("Emplacement", col2X, cursorY);
  value(
    safe(coffret.emplacement),
    col2X,
    cursorY + 12,
    colW
  );

  cursorY += 22;

  label("N° Coffret", titleLeft, cursorY);
  value(
    safe(coffret.numeroCoffret),
    titleLeft,
    cursorY + 12,
    colW
  );

  label("N° Ordre", col2X, cursorY);
  value(
    nStr(coffret.numeroOrdre),
    col2X,
    cursorY + 12,
    colW
  );

// ========================
// Achat / Facturation
// ========================

cursorY += 30;

header(
  "Achat / Facturation",
  titleLeft,
  cursorY
);

cursorY += 14;

// ========================
// TYPE D'ACHAT
// ========================

label(
  "Type d’achat",
  titleLeft,
  cursorY
);

value(
  safe(coffret.typeAchat),
  titleLeft,
  cursorY + 10,
  170
);

// ========================
// DETAILS D'ACHAT
// ========================

label(
  "Détails d’achat",
  col2X,
  cursorY
);

// ✅ largeur plus compacte
const achatLines =
  doc.splitTextToSize(
    safe(coffret.detailsAchat),
    180
  );

// ✅ max 2 lignes seulement
const limitedAchatLines =
  achatLines.slice(0, 2);

doc.setFontSize(10);

doc.setFont(
  "helvetica",
  "normal"
);

doc.setTextColor(
  233,
  241,
  251
);

doc.text(
  limitedAchatLines,
  col2X,
  cursorY + 10
);

// ========================
// HAUTEUR RÉELLE
// ========================

const achatHeight =
  limitedAchatLines.length * 10;

// ========================
// POSITION BAS
// ========================

const contentBottomY =
  cursorY +
  10 +
  achatHeight;

// ========================
// SÉPARATEUR
// ========================

const separatorY =
  contentBottomY + 8;

doc.setDrawColor(
  38,
  47,
  61
);

doc.setLineWidth(0.5);

doc.line(
  titleLeft,
  separatorY,
  cardX + cardW - 20,
  separatorY
);

// ========================
// BAS DU BLOC
// ========================

const bottomRowY =
  separatorY + 14;

// ========================
// PRIX
// ========================

label(
  "Prix d’achat",
  titleLeft,
  bottomRowY
);

value(
  money(coffret.prixAchat),
  titleLeft,
  bottomRowY + 10,
  170
);

// ========================
// FACTURE
// ========================

label(
  "N° Facture",
  col2X,
  bottomRowY
);

value(
  safe(
    coffret.numeroFacture || "SANS"
  ),
  col2X,
  bottomRowY + 10,
  170
);

// ========================
// CURSOR FINAL
// ========================

cursorY =
  bottomRowY + 18;

  // ========================
  // Footer
  // ========================

  doc.setFontSize(9);
  doc.setTextColor(143, 163, 183);

  doc.text(
    `© Musée Reynaud — Export PDF • ${new Date().toLocaleString("fr-FR")}`,
    42,
    pageH - 28
  );

  return doc.output("blob");
}

export async function downloadCoffretPdf(
  coffret: COFFRET
): Promise<void> {
  const blob = await buildCoffretPdf(coffret);

  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");

  const base =
    coffret?.genre || coffret?.espece
      ? `${(coffret.genre || "").trim()}_${(
          coffret.espece || ""
        ).trim()}`
      : `Specimen_${coffret._id}`;

  a.href = url;

  a.download = `${base.replace(/\s+/g, "_")}.pdf`;

  document.body.appendChild(a);

  a.click();

  a.remove();

  URL.revokeObjectURL(url);
}